import React, { createContext, useContext, useEffect, useState } from 'react';
import firebaseAuthService from 'services/FirebaseAuthService';

const FirebaseContext = createContext();

export const useFirebase = () => {
    const context = useContext(FirebaseContext);
    if (!context) {
        throw new Error('useFirebase must be used within a FirebaseProvider');
    }
    return context;
};

export const FirebaseProvider = ({ children }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [firebaseUid, setFirebaseUid] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Initialize Firebase authentication
    useEffect(() => {
        const initFirebase = async () => {
            try {
                setLoading(true);
                setError(null);

                // Set up auth state listener
                const unsubscribe = firebaseAuthService.onAuthStateChanged(async (user) => {
                    if (user) {
                        setIsAuthenticated(true);
                        setFirebaseUid(user.uid);
                    } else {
                        setIsAuthenticated(false);
                        setFirebaseUid(null);
                    }
                    setIsInitialized(true);
                    setLoading(false);
                });

                // Try to get Firebase UID with fallback
                const uid = await firebaseAuthService.getFirebaseUidWithFallback();
                if (uid) {
                    // Firebase UID obtained
                    setFirebaseUid(uid);
                    setIsAuthenticated(true);
                }

                setIsInitialized(true);
                setLoading(false);

                return unsubscribe;
            } catch (error) {
                console.log("❌ [FIREBASE_CONTEXT] Firebase initialization error:", error);
                setError(error);
                setIsInitialized(true);
                setLoading(false);
            }
        };

        initFirebase();
    }, []);

    // Initialize Firebase for dashboard user when they log in
    const initializeForDashboardUser = async (appUserId, userName = null, userType = 'admin', userInfo = {}) => {
        try {
            setLoading(true);
            setError(null);

            const uid = await firebaseAuthService.initializeForDashboardUser(appUserId, userName, userType, userInfo);

            if (uid) {
                // Firebase initialized for dashboard user
                setFirebaseUid(uid);
                setIsAuthenticated(true);
                return uid;
            } else {
                throw new Error('Failed to initialize Firebase for dashboard user');
            }
        } catch (error) {
            console.log("❌ [FIREBASE_CONTEXT] Error initializing for dashboard user:", error);
            setError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Get app user ID from Firebase UID
    const getAppUserIdFromFirebaseUid = async (uid) => {
        try {
            return await firebaseAuthService.getAppUserIdFromFirebaseUid(uid);
        } catch (error) {
            console.log("❌ [FIREBASE_CONTEXT] Error getting app user ID:", error);
            throw error;
        }
    };

    // Get Firebase UID from app user ID
    const getFirebaseUidFromAppUserId = async (appUserId) => {
        try {
            return await firebaseAuthService.getFirebaseUidFromAppUserId(appUserId);
        } catch (error) {
            console.log("❌ [FIREBASE_CONTEXT] Error getting Firebase UID:", error);
            throw error;
        }
    };

    // Update user online status
    const updateUserOnlineStatus = async (appUserId, isOnline, userType = 'admin') => {
        try {
            return await firebaseAuthService.updateUserOnlineStatus(appUserId, isOnline, userType);
        } catch (error) {
            console.log("❌ [FIREBASE_CONTEXT] Error updating user online status:", error);
            throw error;
        }
    };

    // Sign out
    const signOut = async () => {
        try {
            setLoading(true);
            await firebaseAuthService.signOut();
            setIsAuthenticated(false);
            setFirebaseUid(null);
        } catch (error) {
            console.log("❌ [FIREBASE_CONTEXT] Sign out error:", error);
            setError(error);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    // Clear error
    const clearError = () => {
        setError(null);
    };

    // Get auth status
    const getAuthStatus = () => {
        return firebaseAuthService.getAuthStatus();
    };

    const value = {
        // State
        isInitialized,
        isAuthenticated,
        firebaseUid,
        loading,
        error,

        // Actions
        initializeForDashboardUser,
        getAppUserIdFromFirebaseUid,
        getFirebaseUidFromAppUserId,
        updateUserOnlineStatus,
        signOut,
        clearError,
        getAuthStatus,

        // Service methods
        getCurrentFirebaseUid: firebaseAuthService.getCurrentFirebaseUid,
        getFirebaseUidWithFallback: firebaseAuthService.getFirebaseUidWithFallback,
    };

    return (
        <FirebaseContext.Provider value={value}>
            {children}
        </FirebaseContext.Provider>
    );
};


